import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { AdDay } from '@/lib/marketing'
import { supabase, describeError, type Row } from '@/lib/supabase'

export type MetaAd = Row<'meta_ads'>
export type InstagramDay = Row<'instagram_days'>
export type MarketingReview = Row<'marketing_reviews'>
export type Campaign = Row<'marketing_campaigns'>

const keys = {
  ads: ['marketing', 'ads'] as const,
  adDays: (from: string, to: string) => ['marketing', 'ad-days', from, to] as const,
  instagram: (from: string, to: string) => ['marketing', 'instagram', from, to] as const,
  review: ['marketing', 'review'] as const,
  enquiries: (from: string, to: string) => ['marketing', 'enquiries', from, to] as const,
  bookings: (from: string, to: string) => ['marketing', 'bookings', from, to] as const,
  campaigns: ['marketing', 'campaigns'] as const,
}

export function useMetaAds() {
  return useQuery({
    queryKey: keys.ads,
    queryFn: async (): Promise<MetaAd[]> => {
      const { data, error } = await supabase.from('meta_ads').select('*').order('created_time', { ascending: false })
      if (error) throw error
      return data
    },
  })
}

export function useAdDays(from: string, to: string) {
  return useQuery({
    queryKey: keys.adDays(from, to),
    queryFn: async (): Promise<AdDay[]> => {
      const { data, error } = await supabase
        .from('meta_ad_days')
        .select('ad_id, day, spend, impressions, reach, clicks, link_clicks, engagement, messages, leads')
        .gte('day', from)
        .lte('day', to)
        .order('day')
      if (error) throw error
      return data.map((row) => ({ ...row, spend: Number(row.spend) }))
    },
  })
}

export function useInstagramDays(from: string, to: string) {
  return useQuery({
    queryKey: keys.instagram(from, to),
    queryFn: async (): Promise<InstagramDay[]> => {
      const { data, error } = await supabase.from('instagram_days').select('*').gte('day', from).lte('day', to).order('day')
      if (error) throw error
      return data
    },
  })
}

export function useLatestReview() {
  return useQuery({
    queryKey: keys.review,
    queryFn: async (): Promise<MarketingReview | null> => {
      const { data, error } = await supabase.from('marketing_reviews').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle()
      if (error) throw error
      return data
    },
  })
}

export type MarketingEnquiry = Pick<Row<'inquiries'>, 'id' | 'received_at' | 'channel' | 'lead_source' | 'status'>

/** Enquiries received in the period, spam left out. */
export function useMarketingEnquiries(from: string, to: string) {
  return useQuery({
    queryKey: keys.enquiries(from, to),
    queryFn: async (): Promise<MarketingEnquiry[]> => {
      const { data, error } = await supabase
        .from('inquiries')
        .select('id, received_at, channel, lead_source, status')
        .gte('received_at', `${from}T00:00:00+02:00`)
        .lte('received_at', `${to}T23:59:59+02:00`)
        .neq('status', 'spam')
      if (error) throw error
      return data
    },
  })
}

export interface MarketingBooking {
  id: string
  booked_at: string
  agreed_total: number | null
  inquiry: { lead_source: string | null } | null
}

/**
 * Weddings booked in the period. Bookings before 16 Oct 2026 were dated when
 * the Studio was filled in, not when they booked, so they are left out.
 */
export function useMarketingBookings(from: string, to: string) {
  return useQuery({
    queryKey: keys.bookings(from, to),
    queryFn: async (): Promise<MarketingBooking[]> => {
      const start = from > '2026-10-16' ? from : '2026-10-16'
      const { data, error } = await supabase
        .from('jobs')
        .select('id, booked_at, agreed_total, inquiry:inquiries!jobs_inquiry_id_fkey(lead_source)')
        .gte('booked_at', `${start}T00:00:00+02:00`)
        .lte('booked_at', `${to}T23:59:59+02:00`)
      if (error) throw error
      return data as unknown as MarketingBooking[]
    },
  })
}

export function useCampaigns() {
  return useQuery({
    queryKey: keys.campaigns,
    queryFn: async (): Promise<Campaign[]> => {
      const { data, error } = await supabase.from('marketing_campaigns').select('*').order('starts_on', { ascending: false, nullsFirst: true })
      if (error) throw error
      return data
    },
  })
}

export type CampaignInput = Pick<Campaign, 'name' | 'kind' | 'status' | 'starts_on' | 'ends_on' | 'offer' | 'audience' | 'notes'>

export function useSaveCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id: string | null; input: CampaignInput }) => {
      const { error } = id
        ? await supabase.from('marketing_campaigns').update(input).eq('id', id)
        : await supabase.from('marketing_campaigns').insert(input)
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Saved')
      void queryClient.invalidateQueries({ queryKey: keys.campaigns })
    },
    onError: (error) => toast.error('Could not save that', { description: describeError(error) }),
  })
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('marketing_campaigns').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.campaigns }),
    onError: (error) => toast.error('Could not remove that', { description: describeError(error) }),
  })
}

export const CHANNEL_LABEL: Record<string, string> = {
  website: 'Website form',
  email: 'Email',
  whatsapp: 'WhatsApp',
  instagram: 'Instagram DM',
  facebook: 'Facebook',
  phone: 'Phone',
  in_person: 'In person',
  other: 'Other',
}

export const SOURCE_LABEL: Record<string, string> = {
  meta_ads: 'Meta ads',
  instagram: 'Instagram',
  facebook: 'Facebook',
  google: 'Google',
  website: 'Website',
  referral: 'Referral',
  vendor: 'Vendor or planner',
  repeat_client: 'Repeat client',
  wedding_fair: 'Wedding fair',
  other: 'Other',
}

export const CAMPAIGN_KIND_LABEL: Record<Campaign['kind'], string> = {
  ads: 'Ads',
  promotion: 'Promotion',
  mini_sessions: 'Mini sessions',
  vendor_outreach: 'Vendor outreach',
  content: 'Content',
  other: 'Other',
}

export const CAMPAIGN_STATUS_LABEL: Record<Campaign['status'], string> = {
  idea: 'Idea',
  planned: 'Planned',
  running: 'Running',
  done: 'Done',
}
